import { isFalsy } from "./isFalsy";

export default function formatUrl(url: string): string {
  const urlWithoutTrailingSlash = url.replace(/\/+$/, "");
  const httpsUrl = `https://${urlWithoutTrailingSlash}`;

  const startsWithwww = urlWithoutTrailingSlash.startsWith("www");

  if (urlWithoutTrailingSlash.startsWith("/")) {
    return urlWithoutTrailingSlash;
  }

  // add http if url does not have http or https prefix
  if (!/^https?:\/\//i.test(urlWithoutTrailingSlash) || startsWithwww) {
    return httpsUrl;
  }

  if (!/^https?:\/\//i.test(urlWithoutTrailingSlash) && !startsWithwww) {
    return httpsUrl;
  }

  return urlWithoutTrailingSlash;
}

const getUrlForParsing = (url: string): string => {
  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  if (url.startsWith("//")) {
    return `https:${url}`;
  }

  return `https://${url}`;
};

const encodeBase64 = (value: string): string => {
  if (typeof window !== "undefined" && typeof window.btoa === "function") {
    return window.btoa(value);
  }

  return Buffer.from(value, "utf8").toString("base64");
};

export function getUrlHostname(url?: string | null): string {
  if (!url || typeof url !== "string") {
    return "";
  }

  try {
    return new URL(getUrlForParsing(url)).hostname;
  } catch (_error) {
    return "";
  }
}

export function getUrlHost(url?: string | null): string {
  if (!url || typeof url !== "string") {
    return "";
  }

  try {
    return new URL(getUrlForParsing(url)).host;
  } catch (_error) {
    return "";
  }
}

export function getUrlOrigin(url?: string | null): string {
  if (!url || typeof url !== "string") {
    return "";
  }

  try {
    return new URL(getUrlForParsing(url)).origin;
  } catch (_error) {
    return "";
  }
}

export function isLocalhostHostname(hostname?: string | null): boolean {
  return hostname === "localhost";
}

export function normalizeUrlProtocolForHostname({
  url,
  hostname,
  protocol = "https:",
}: {
  url?: string | null;
  hostname?: string | null;
  protocol?: "http:" | "https:";
}): string | null | undefined {
  if (
    !url ||
    typeof url !== "string" ||
    !hostname ||
    !/^https?:\/\//i.test(url)
  ) {
    return url;
  }

  try {
    const normalizedUrl = new URL(url);

    if (
      normalizedUrl.hostname === hostname &&
      normalizedUrl.protocol !== protocol
    ) {
      normalizedUrl.protocol = protocol;
      return normalizedUrl.toString();
    }
  } catch (_error) {
    return url;
  }

  return url;
}

export function replaceEncodedReturnUrl({
  url,
  returnUrl,
}: {
  url?: string | null;
  returnUrl?: string | null;
}): string | null | undefined {
  if (!url || typeof url !== "string" || !returnUrl) {
    return url;
  }

  try {
    const parsedUrl = new URL(url);

    if (!parsedUrl.searchParams.has("returnUrl")) {
      return url;
    }

    parsedUrl.searchParams.set("returnUrl", encodeBase64(returnUrl));

    return parsedUrl.toString();
  } catch (_error) {
    return url;
  }
}

export function formatTripmanagerDeeplink({
  reservationId,
  surname,
}: {
  reservationId: string;
  surname: string;
}): { sourceUrl: string; restrictUrl: string } {
  if (isFalsy(reservationId) && isFalsy(surname)) {
    throw new Error("Cannot format URL from empty reservationId and surname");
  }

  if (!reservationId || typeof reservationId !== "string") {
    throw new Error("reservationId must be a non-empty string");
  }

  if (!surname || typeof surname !== "string") {
    throw new Error("surname must be a non-empty string");
  }

  const deepLinkURL = `https://www.tripmanager.com/deeplink?locator=${reservationId}&passenger_name=${surname}`;

  // Extract the hostname manually
  const urlParts = deepLinkURL.match(/^(https?:\/\/[^/]+)(.*)/);
  const restrictUrl = urlParts ? `${urlParts[1]}/` : "";

  return {
    sourceUrl: deepLinkURL,
    restrictUrl,
  };
}
